import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useEmblaCarousel from 'embla-carousel-react';
import { useDrag } from '@use-gesture/react';
import { IconCompass } from '@tabler/icons-react';
import { Button } from '@/components/Button';
import { useAuth } from '@/store/auth';
import { useFeed } from '@/store/feed';
import { Card } from '@/features/card/Card';
import { ProgressBar } from '@/components/ProgressBar';
import { Skeleton } from '@/components/Skeleton';
import { resolveIndexChange } from '@/features/feed/feedSwipe';
import { cardShownOnView } from '@/features/feed/feedSeen';
import { SwipeHintBar } from '@/features/feed/SwipeHintOverlay';
import { api } from '@/api/endpoints';
import { ApiException } from '@/api/client';
import { useUI } from '@/store/ui';
import { haptic } from '@/lib/telegram';
import { shareCard } from '@/lib/shareCard';
import { ENV } from '@/lib/env';
import { isAdCard, type FeedItem } from '@/api/types';
import { AdCard } from '@/features/ads/AdCard';
import { trackEvent } from '@/lib/analytics';

export default function Feed() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const showToast = useUI(s => s.showToast);
  const myRefCode = useAuth(s => s.user?.ref_code ?? null);
  const swipeHintSeen = useAuth(s => s.user?.swipe_hint_seen ?? true);
  const patchUser = useAuth(s => s.patchUser);
  const [hintSeenCount, setHintSeenCount] = useState(0);
  const hintActive = !swipeHintSeen && hintSeenCount < 10;

  const dismissHint = () => {
    patchUser({ swipe_hint_seen: true });
    void api.me.setSwipeHintSeen().catch(() => { /* не критично */ });
  };

  const { items, cursor, meta, loading, load, prefetchIfNeeded, applyDelta, setUserAction, setSeenProgress, setCursor } = useFeed();

  // Грузим только при первом заходе.
  useEffect(() => {
    if (items.length === 0) void load(10);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { void prefetchIfNeeded(); }, [cursor, prefetchIfNeeded]);
  useEffect(() => {
    if (!loading && meta?.daily_limit_reached) nav('/limit');
  }, [loading, meta, nav]);

  const current = items[cursor];

  // --- Embla (вертикаль) ---
  const startIndexRef = useRef(cursor);
  const [emblaRef, emblaApi] = useEmblaCarousel({
    axis: 'y',
    startIndex: startIndexRef.current,
    skipSnaps: false,
    dragThreshold: 12,
  });
  const lastIndexRef = useRef(startIndexRef.current);

  /**
   * Отметить ПОКАЗАННУЮ карточку как seen (on-view). Считаем при показе, а не при уходе:
   * последнюю карту ленты покинуть нельзя (Embla некуда листать), при on-leave она не
   * засчитывалась → лимит замирал на (limit−1), и экран лимита был недостижим. Идемпотентно
   * через hasSeen-гард. После засчёта дожидаемся prefetch: если это была последняя карта из
   * дневного лимита, buildFeed вернёт remaining=0 → meta.daily_limit_reached=true → эффект
   * увезёт на /limit (детерминированно, без гонки с cursor-эффектом; premium-safe — у
   * премиума buildFeed лимит не отдаёт).
   */
  const markShownSeen = async (shownCard: FeedItem) => {
    if (useFeed.getState().hasSeen(shownCard.id)) return;
    try {
      const { data } = await api.feed.seen(shownCard.id);
      useFeed.getState().markSeen(shownCard.id);
      setSeenProgress(data.cards_seen, data.limit);
      if (data.daily_limit_reached) { nav('/limit'); return; }
      await prefetchIfNeeded();
    } catch (e) {
      if (e instanceof ApiException && e.status === 429) { nav('/limit'); return; }
      showToast(t('errors.network'), 'error');
    }
  };

  // On-view: засчитываем карту, на которой стоит юзер (стартовую, каждую следующую и
  // последнюю). hasSeen-гард делает вызов идемпотентным, так что повторные срабатывания
  // при догрузке/ремаунте безопасны.
  useEffect(() => {
    const shown = cardShownOnView(items, cursor);
    if (shown) void markShownSeen(shown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cursor, items.length]);

  // Подписка на смену слайда Embla.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      const idx = emblaApi.selectedScrollSnap();
      const change = resolveIndexChange(items, lastIndexRef.current, idx);
      lastIndexRef.current = idx;
      setCursor(idx);
      if (!change) return;
      trackEvent('card_swipe', { card_id: change.leftCard.id, direction: change.direction });
      // seen-отметку делает on-view эффект по cursor; здесь — только haptic/hint на свайп вверх.
      if (change.direction === 'up') {
        haptic.light();
        if (hintActive) {
          const n = hintSeenCount + 1;
          setHintSeenCount(n);
          if (n >= 10) dismissHint();
        }
      }
    };
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, items]);

  // Дозагрузка добавляет слайды в конец → reInit пересчитывает раскладку; при чистом
  // append Embla удерживает текущую позицию (соседние слайды не двигаются).
  useEffect(() => {
    emblaApi?.reInit();
  }, [emblaApi, items.length]);

  // Стор сбросили (re-auth / смена языка) — items опустели. Возвращаем Embla и refs
  // в начало, иначе внутренний индекс Embla и lastIndexRef остаются на старой позиции
  // и после новой загрузки cursor/слайд рассинхронятся.
  useEffect(() => {
    if (items.length === 0) {
      lastIndexRef.current = 0;
      startIndexRef.current = 0;
      emblaApi?.scrollTo(0, true);
    }
  }, [items.length, emblaApi]);

  // --- Left-свайп: открыть книгу (независимый горизонтальный жест) ---
  const goBook = () => {
    if (current && !isAdCard(current)) {
      trackEvent('card_swipe', { card_id: current.id, direction: 'left' });
      nav(`/book/${current.book.id}`);
    }
  };
  const bindLeft = useDrag(({ last, movement: [mx, my], velocity: [vx], canceled }) => {
    if (last && !canceled && mx < -80 && Math.abs(mx) > Math.abs(my) && (vx > 0.5 || mx < -140)) {
      goBook();
    }
  }, { axis: 'x', filterTaps: true });

  const onAction = async (card: FeedItem, action: 'like' | 'dislike' | 'bookmark' | 'share') => {
    if (isAdCard(card)) return;
    const c = card;
    trackEvent('tap', { element: action, card_id: c.id });
    haptic.light();

    if (action === 'like') {
      const next = !c.user_actions.liked;
      applyDelta(c.id, { likes: next ? +1 : -1, dislikes: c.user_actions.disliked && next ? -1 : 0 });
      setUserAction(c.id, 'liked', next);
      if (c.user_actions.disliked && next) setUserAction(c.id, 'disliked', false);
    }
    if (action === 'dislike') {
      const next = !c.user_actions.disliked;
      applyDelta(c.id, { dislikes: next ? +1 : -1, likes: c.user_actions.liked && next ? -1 : 0 });
      setUserAction(c.id, 'disliked', next);
      if (c.user_actions.liked && next) setUserAction(c.id, 'liked', false);
    }
    if (action === 'bookmark') {
      const next = !c.user_actions.bookmarked;
      applyDelta(c.id, { bookmarks: next ? +1 : -1 });
      setUserAction(c.id, 'bookmarked', next);
    }
    if (action === 'share') {
      applyDelta(c.id, { shares: 1 });
      const quote = c.text ?? '';
      const startParam = `card_${c.id}${myRefCode ? `__ref_${myRefCode}` : ''}`;
      const url = `https://t.me/${ENV.BOT_USERNAME}?startapp=${startParam}`;
      const text = t('feed.share_text', { quote, author: c.book.author, title: c.book.title, url });
      void shareCard(c.id, { url, text });
    }

    try {
      await api.cards.toggle(c.id, action);
    } catch {
      if (action === 'like') {
        const wasOn = !c.user_actions.liked;
        applyDelta(c.id, { likes: wasOn ? -1 : +1 });
        setUserAction(c.id, 'liked', !wasOn);
        if (c.user_actions.disliked && wasOn) {
          applyDelta(c.id, { dislikes: +1 });
          setUserAction(c.id, 'disliked', true);
        }
      } else if (action === 'dislike') {
        const wasOn = !c.user_actions.disliked;
        applyDelta(c.id, { dislikes: wasOn ? -1 : +1 });
        setUserAction(c.id, 'disliked', !wasOn);
        if (c.user_actions.liked && wasOn) {
          applyDelta(c.id, { likes: +1 });
          setUserAction(c.id, 'liked', true);
        }
      } else if (action === 'bookmark') {
        const wasOn = !c.user_actions.bookmarked;
        applyDelta(c.id, { bookmarks: wasOn ? -1 : +1 });
        setUserAction(c.id, 'bookmarked', !wasOn);
      } else if (action === 'share') {
        applyDelta(c.id, { shares: -1 });
      }
      showToast(t('errors.generic'), 'error');
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="flex-1 flex flex-col p-4 gap-4">
        <Skeleton height={4} className="w-2/3" rounded="pill" />
        <Skeleton height={300} className="rounded-xl" />
      </div>
    );
  }

  if (!current) {
    if (meta?.daily_limit_reached) {
      return (
        <div className="flex-1 flex flex-col p-4 gap-4">
          <Skeleton height={4} className="w-2/3" rounded="pill" />
          <Skeleton height={300} className="rounded-xl" />
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-center px-6 gap-4">
        <div className="w-20 h-20 rounded-pill bg-dark text-text-inverse grid place-items-center">
          <IconCompass size={36} />
        </div>
        <h1 className="font-content text-[28px]">{t('feed.empty_title')}</h1>
        <p className="text-text-muted max-w-sm">{t('feed.empty_sub')}</p>
        <div className="w-full max-w-sm flex flex-col gap-2 mt-2">
          <Button block onClick={() => nav('/topics')}>{t('feed.empty_open_topics')}</Button>
          <Button block variant="secondary" onClick={() => void load(10)}>{t('feed.empty_refresh')}</Button>
        </div>
      </div>
    );
  }

  const cardsSeen = meta?.daily_progress?.cards_seen ?? 0;
  const limit = meta?.daily_progress?.limit ?? 30;

  return (
    <div className="relative flex-1 min-h-0 flex flex-col bg-bg" {...bindLeft()} style={{ touchAction: 'pan-y' }}>
      <header className="px-4 pt-3 flex items-center gap-3">
        <ProgressBar value={cardsSeen / limit} className="flex-1" />
        <div className="text-xs text-text-muted whitespace-nowrap">
          <span className="text-text font-medium">{t('feed.limit_left', { n: cardsSeen, max: limit })}</span>
          <span className="ml-2">{t('feed.limit_remaining')}</span>
        </div>
      </header>
      {hintActive && (
        <div className="mt-2">
          <SwipeHintBar direction="right" onDismiss={dismissHint} />
        </div>
      )}

      {/* Embla viewport */}
      <div className="flex-1 min-h-0 overflow-hidden" ref={emblaRef}>
        <div className="h-full flex flex-col">
          {items.map((it) => (
            <div key={it.id} className="flex-[0_0_100%] min-h-0 flex flex-col">
              {isAdCard(it)
                ? <AdCard ad={it} onSubscribe={() => nav('/subscription')} />
                : <Card
                    card={it}
                    onLike={() => onAction(it, 'like')}
                    onDislike={() => onAction(it, 'dislike')}
                    onBookmark={() => onAction(it, 'bookmark')}
                    onShare={() => onAction(it, 'share')}
                    onComments={() => nav(`/card/${it.id}/comments`)}
                  />
              }
            </div>
          ))}
        </div>
      </div>

      {hintActive && (
        <div className="pb-3">
          <SwipeHintBar direction="down" onDismiss={dismissHint} />
        </div>
      )}
    </div>
  );
}
